(() => {
  'use strict';

  const ORIGIN = 'https://92abe6-ad.myconvermax.com';
  const exact = value => typeof value === 'string' ? value.trim().toLocaleLowerCase('en-US') : '';
  const isScalar = value => typeof value === 'string' || typeof value === 'number';

  function requestUrl(productId) {
    if (!/^[1-9]\d{3,19}$/.test(String(productId))) throw new Error('INVALID_PRODUCT');
    const url = new URL('/ymm/fitments.json', ORIGIN);
    url.searchParams.set('productId', String(productId));
    return url.href;
  }

  function yearMatches(value, year) {
    const match = /^(\d{4})(?:\s*[-–]\s*(\d{4}))?$/.exec(String(value ?? '').trim());
    if (!match || !Number.isInteger(Number(year))) return false;
    const first = Number(match[1]), last = Number(match[2] || match[1]);
    return first >= 1886 && first <= last && last <= 2200 && Number(year) >= first && Number(year) <= last;
  }

  function normalizeCatalog(value) {
    if (!value || typeof value !== 'object' || !Array.isArray(value.Fields) || !Array.isArray(value.Fitments) || value.Fields.length > 40 || value.Fitments.length > 10000) throw new Error('INVALID_CATALOG');
    const fields = [];
    for (const field of value.Fields) {
      if (!field || typeof field.Name !== 'string' || !field.Name || field.Name.length > 80 || fields.some(item => item.name === field.Name)) throw new Error('INVALID_FIELDS');
      fields.push({ name: field.Name, title: typeof field.Title === 'string' && field.Title ? field.Title : field.Name });
    }
    const rows = value.Fitments.map(row => {
      if (!row || typeof row !== 'object' || Array.isArray(row)) throw new Error('INVALID_ROW');
      const clean = Object.create(null);
      for (const [key, cell] of Object.entries(row)) {
        if (!isScalar(cell) && cell != null) throw new Error('INVALID_CELL');
        if (key.length > 80 || String(cell ?? '').length > 16000) throw new Error('INVALID_CELL');
        clean[key] = cell == null ? '' : String(cell);
        if (!fields.some(field => field.name === key)) fields.push({ name: key, title: key });
      }
      return clean;
    });
    if (fields.length > 40) throw new Error('INVALID_FIELDS');
    const fitment = exact(value.Fitment).replace(/[ -]/g, '');
    const kind = fitment === 'vehiclespecific' ? 'specific' : fitment === 'universal' || fitment === 'universalfit' ? 'universal' : 'unknown';
    return { fields, rows, kind, incomplete: rows.length >= 1000 };
  }

  function matchingRows(catalog, vehicle) {
    if (!vehicle || catalog.kind !== 'specific') return [];
    return catalog.rows.filter(row => yearMatches(row.Year, vehicle.year) && exact(row.Make) === exact(vehicle.make) && exact(row.Model) === exact(vehicle.model));
  }

  function vehicleKey(vehicle) {
    return vehicle && Number.isInteger(Number(vehicle.year)) && exact(vehicle.make) && exact(vehicle.model)
      ? JSON.stringify([vehicle.id || '', Number(vehicle.year), vehicle.make, vehicle.model, vehicle.updatedAt || '']) : '';
  }

  function applicationView(catalog, vehicle) {
    if (!vehicleKey(vehicle)) return { state: 'empty', rows: [] };
    if (!catalog) return { state: 'unknown', rows: [] };
    if (catalog.kind !== 'specific') return { state: catalog.kind, rows: [] };
    const rows = matchingRows(catalog, vehicle);
    return { state: rows.length ? 'matching' : 'no_rows', rows };
  }

  function createGate() {
    let revision = 0;
    return { next: () => ++revision, current: token => token === revision };
  }

  const api = { requestUrl, yearMatches, normalizeCatalog, matchingRows, vehicleKey, applicationView, createGate };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window === 'undefined' || typeof HTMLElement === 'undefined') return;
  if (customElements.get('sac-garage-parts-fitment')) return;

  class GaragePartsFitment extends HTMLElement {
    connectedCallback() {
      if (this.connected) return;
      this.connected = true;
      this.gate = createGate();
      this.showAll = false;
      this.owner = this.dataset.productId;
      this.onClick = event => {
        if (event.target.closest('[data-parts-retry]')) this.load();
        if (event.target.closest('[data-parts-toggle]')) this.toggleApplications();
      };
      this.addEventListener('click', this.onClick);
      this.chartObserver = new MutationObserver(() => this.refreshNativeChart());
      this.chartObserver.observe(document.body, { childList: true, subtree: true });
      this.refreshNativeChart();
      this.onReady = () => this.bind();
      document.addEventListener('sac:garage:ready', this.onReady);
      this.bind();
      let attempts = 0;
      if (!this.unsubscribe) this.readyTimer = setInterval(() => {
        if (this.bind() || ++attempts >= 40) clearInterval(this.readyTimer);
      }, 250);
    }

    disconnectedCallback() {
      this.connected = false;
      this.gate?.next();
      this.requestController?.abort();
      this.unsubscribe?.();
      this.unsubscribe = null;
      this.selectionKey = undefined;
      clearInterval(this.readyTimer);
      cancelAnimationFrame(this.scrollFrame);
      this.chartObserver?.disconnect();
      document.removeEventListener('sac:garage:ready', this.onReady);
      this.removeEventListener('click', this.onClick);
      this.releaseNative();
    }

    refreshNativeChart() {
      const chart = document.getElementById('cm-fitment-table');
      if (chart === this.nativeChart) return;
      this.nativeChart = chart;
      if (!chart) {
        this.showAll = false;
        if (this.vehicle) document.documentElement.dataset.sacGaragePartsAll = 'false';
      }
      const toggle = this.querySelector('[data-parts-toggle]');
      if (toggle) {
        toggle.hidden = !chart;
        toggle.textContent = this.label(this.showAll ? 'hide_applications' : 'all_applications');
        toggle.setAttribute('aria-expanded', String(this.showAll));
      }
    }

    toggleApplications() {
      this.refreshNativeChart();
      if (!this.nativeChart) return;
      this.showAll = !this.showAll;
      this.render();
      cancelAnimationFrame(this.scrollFrame);
      if (!this.showAll) {
        this.querySelector('[data-parts-toggle]')?.focus({ preventScroll: true });
        return;
      }
      const chart = this.nativeChart, ancestors = [];
      for (let parent = chart.parentElement; parent; parent = parent.parentElement) ancestors.unshift(parent);
      for (const parent of ancestors) {
        if (parent.matches('details') && !parent.open) {
          const summary = parent.querySelector(':scope > summary');
          if (summary) summary.click(); else parent.open = true;
        }
        if (parent.matches('[role="tabpanel"]') && parent.id) {
          const scope = parent.closest('tabbed-content') || document;
          const tab = [...scope.querySelectorAll('[role="tab"][aria-controls]')].find(item => item.getAttribute('aria-controls') === parent.id);
          if (tab && (parent.hidden || tab.getAttribute('aria-selected') !== 'true')) tab.click();
        }
      }
      this.scrollFrame = requestAnimationFrame(() => {
        if (this.connected && this.showAll && chart === this.nativeChart && chart.isConnected) chart.scrollIntoView({ block: 'start', behavior: 'auto' });
      });
    }

    bind() {
      if (this.unsubscribe) return true;
      const bridge = window.SacGarageSelection;
      if (!bridge || typeof bridge.getVehicle !== 'function' || typeof bridge.subscribe !== 'function') return false;
      this.unsubscribe = bridge.subscribe(vehicle => this.select(vehicle));
      this.select(bridge.getVehicle());
      return true;
    }

    select(vehicle) {
      const key = vehicleKey(vehicle);
      if (key === this.selectionKey) return;
      this.selectionKey = key;
      this.vehicle = key ? vehicle : null;
      this.showAll = false;
      cancelAnimationFrame(this.scrollFrame);
      this.gate.next();
      this.requestController?.abort();
      this.loading = false;
      this.error = false;
      this.hidden = !this.vehicle;
      if (!this.vehicle) { this.releaseNative(); return; }
      document.documentElement.dataset.sacGaragePartsActive = this.owner;
      if (this.catalog) this.render(); else this.load();
    }

    releaseNative() {
      if (document.documentElement.dataset.sacGaragePartsActive === this.owner) {
        delete document.documentElement.dataset.sacGaragePartsActive;
        delete document.documentElement.dataset.sacGaragePartsAll;
      }
    }

    async load() {
      if (!this.vehicle || !this.connected) return;
      const token = this.gate.next();
      this.requestController?.abort();
      const controller = new AbortController();
      this.requestController = controller;
      this.loading = true;
      this.error = false;
      this.render();
      const timeout = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(requestUrl(this.owner), { method: 'GET', credentials: 'omit', mode: 'cors', cache: 'no-store', redirect: 'error', referrerPolicy: 'no-referrer', signal: controller.signal, headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error('CATALOG_UNAVAILABLE');
        const text = await response.text();
        if (text.length > 2 * 1024 * 1024) throw new Error('CATALOG_TOO_LARGE');
        const catalog = normalizeCatalog(JSON.parse(text));
        if (!this.connected || !this.gate.current(token)) return;
        this.catalog = catalog;
      } catch {
        if (!this.connected || !this.gate.current(token)) return;
        this.error = true;
      } finally {
        clearTimeout(timeout);
        if (this.connected && this.gate.current(token)) { this.loading = false; this.render(); }
      }
    }

    label(name) { return this.dataset[`label${name.replace(/(^|_)(\w)/g, (_, prefix, letter) => letter.toUpperCase())}`] || ''; }
    node(tag, text, className) {
      const node = document.createElement(tag);
      if (text) node.textContent = text;
      if (className) node.className = className;
      return node;
    }

    render() {
      if (!this.vehicle || !this.connected) return;
      this.replaceChildren();
      this.setAttribute('aria-busy', String(this.loading));
      this.append(this.node('h2', this.label('title'), 'sac-garage-parts__heading'));
      const car = [this.vehicle.year, this.vehicle.make, this.vehicle.model].join(' ');
      this.append(this.node('p', `${this.label('context')} ${car}`, 'sac-garage-parts__context'));
      const status = this.node('p', this.label(this.loading ? 'loading' : this.error ? 'unavailable' : applicationView(this.catalog, this.vehicle).state));
      status.setAttribute('role', 'status');
      this.append(status);
      if (this.error) {
        const retry = this.node('button', this.label('retry'), 'sac-garage-parts__button');
        retry.type = 'button'; retry.dataset.partsRetry = ''; this.append(retry);
      }
      if (!this.loading && !this.error && this.catalog) {
        const view = applicationView(this.catalog, this.vehicle);
        if (view.rows.length) this.append(this.table(view.rows));
        if (this.catalog.incomplete) this.append(this.node('p', this.label('incomplete')));
      }
      this.append(this.node('p', this.label('check_notes'), 'sac-garage-parts__note'));
      const toggle = this.node('button', this.label(this.showAll ? 'hide_applications' : 'all_applications'), 'sac-garage-parts__button');
      toggle.type = 'button'; toggle.dataset.partsToggle = ''; toggle.setAttribute('aria-expanded', String(this.showAll)); toggle.setAttribute('aria-controls', 'cm-fitment-table');
      toggle.hidden = !this.nativeChart;
      this.append(toggle);
      document.documentElement.dataset.sacGaragePartsAll = String(this.showAll);
    }

    table(rows) {
      const wrapper = this.node('div', '', 'sac-garage-parts__table-wrap');
      wrapper.tabIndex = 0;
      wrapper.setAttribute('role', 'region'); wrapper.setAttribute('aria-label', this.label('matching'));
      const table = this.node('table', '', 'sac-garage-parts__table'), head = this.node('thead'), heading = this.node('tr');
      for (const field of this.catalog.fields) { const cell = this.node('th', field.title); cell.scope = 'col'; heading.append(cell); }
      head.append(heading); table.append(head);
      const body = this.node('tbody');
      for (const row of rows) {
        const tr = this.node('tr');
        for (const field of this.catalog.fields) tr.append(this.node('td', row[field.name] || '—'));
        body.append(tr);
      }
      table.append(body); wrapper.append(table); return wrapper;
    }
  }
  customElements.define('sac-garage-parts-fitment', GaragePartsFitment);
})();
